import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { STALE_TIME, CACHE_TIME } from '~/common/constants/react-query.const';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { FEATURE } from '~/common/enums/feature.enum';
import { UserContext } from '~/components/providers/UserProvider';
import PromotionsModalsContextProvider from '~/domains/promotions/Modals/PromotionsModalsProvider';

import { GamificationQueryKeys } from '../query';
import { getLoyaltyPrograms, getLoyaltyProgramStatus } from '../services/gamification.service.api';
import type { LoyaltyProgram, LoyaltyProgramStatus } from '../types/gamification.types';
import MissionsRealtimeManager from '../components/MissionsRealtimeManager/MissionsRealtimeManager';

interface GamificationContextValue {
  activeProgram: LoyaltyProgram;
  activeProgramStatus: LoyaltyProgramStatus;
  isLoyaltyProgramsLoading: boolean;
  isActiveProgramStatusLoading: boolean;
  refetchActiveProgramStatus: () => void;
}

const GamificationContext = createContext<GamificationContextValue | undefined>(undefined);

function GamificationContextProvider({ children }: React.PropsWithChildren) {
  const user = useContext(UserContext);
  const isWhalersClubEnabled = useIsFeatureEnabled(FEATURE.ENABLE_WHALERS_CLUB);
  const [activeProgramId, setActiveProgramId] = useState<string | null>(null);

  const { data: loyaltyPrograms, isLoading: isLoyaltyProgramsLoading } = useQuery({
    queryKey: GamificationQueryKeys.PROGRAMS,
    queryFn: getLoyaltyPrograms,
    staleTime: STALE_TIME.DEFAULT,
    cacheTime: CACHE_TIME.DEFAULT,
    enabled: !!user?.userId && isWhalersClubEnabled,
  });

  const activeProgram = useMemo(
    () =>  loyaltyPrograms?.data.find((program) => program.slug === 'whalers-club') ?? null
    , [loyaltyPrograms]);

  useEffect(() => {
    setActiveProgramId(activeProgram?.id ?? null);
  }, [activeProgram]);

  const {
    data: activeProgramStatus,
    isLoading: isActiveProgramStatusLoading,
    refetch: refetchActiveProgramStatus,
  } = useQuery({
    queryKey: GamificationQueryKeys.ACTIVE_PROGRAM_STATUS(activeProgramId),
    queryFn: () => getLoyaltyProgramStatus(activeProgramId),
    enabled: !!activeProgramId,
    staleTime: STALE_TIME.QUICKPICKS,
  });

  const value = useMemo<GamificationContextValue>(() => ({
    activeProgram,
    activeProgramStatus,
    isLoyaltyProgramsLoading,
    isActiveProgramStatusLoading,
    refetchActiveProgramStatus,
  }), [
    activeProgram,
    activeProgramStatus,
    isLoyaltyProgramsLoading,
    isActiveProgramStatusLoading,
    refetchActiveProgramStatus,
  ]);

  return <GamificationContext.Provider value={value}>
    <PromotionsModalsContextProvider>
      <MissionsRealtimeManager />
      {children}
    </PromotionsModalsContextProvider>
  </GamificationContext.Provider>;
}

function useGamificationContext() {
  const context = useContext(GamificationContext);

  if (context === undefined) {
    throw new Error('useGamificationContext must be used within a GamificationContextProvider');
  }

  return context;
}

export { GamificationContext, GamificationContextProvider, useGamificationContext };
